import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";

import { compose, graphql } from "lib/apolloClient";
import { useI18n } from "Hooks";

import { Modal, Button, ToggleSwitch, RadioButton } from "@toddle-edu/ds-web";

import { FormTextInput } from "UIComponents/InputTextField";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader/GqlWithLoader";

import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";

import {
  ORGANIZATION,
  ATTENDANCE_SYNC,
  ATTENDANCE_SYNC_SUB_TYPES,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants.js";
import { updateAddOnStatus } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import { getInternalToolGenericDataVariables } from "AddOnManager/modules/AddOnManagerUtils";

import classes from "./AttendanceSync.scss";

const AttendanceSyncWrapper = props => {
  const {
    orgId,
    updateAddOnStatus,
    internalToolGenericDataVariables,
    configValues,
    attSyncDirection,
    onSuccessAddOnConfigChange,
  } = props;
  const { t } = useI18n(["integration", "common"]);

  // assign default value to the state
  // rendered at first refresh // TO check with timetable query
  const [orgLevelToggleMap, setOrgLevelToggleMap] = useState({
    [ATTENDANCE_SYNC_SUB_TYPES.BASE_SYNC]: _.get(
      configValues,
      `syncModule`,
      false
    ),
    [ATTENDANCE_SYNC_SUB_TYPES.LR_ARCH_ENABLE]: _.get(
      configValues,
      `newIntegrationLRArchitecture`,
      false
    ),
  });

  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [syncDirection, setSyncDirection] = useState(attSyncDirection);

  const handleOrgLevelToggle = async ({ addOnSubType, syncDirection }) => {
    setFullScreenLoader(true);
    setOrgLevelToggleMap(prev => ({
      ...prev,
      [addOnSubType]: !prev[addOnSubType],
    }));
    await updateAddOnStatus({
      orgId,
      addOnType: ATTENDANCE_SYNC,
      updateOn: ORGANIZATION,
      newStatus: !orgLevelToggleMap[addOnSubType],
      queryVariables: internalToolGenericDataVariables,
      addOnSubType,
      additionalInput: { syncDirection },
    });
    setFullScreenLoader(false);
    if (!_.isNil(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
  };

  return (
    <div className={classes.container}>
      <FormTextInput
        size="large"
        label={t("common:org_id")}
        error=""
        value={orgId}
        isRequired={true}
        disabled={true}
      />

      <div className={classes.switchContainer}>
        <div>{t("integration:choose_sync_direction_label")}</div>
        <div className={classes.actionItems}>
          <RadioButton
            key={"PULL"}
            onChange={() => {
              setSyncDirection("PULL");
            }}
            checked={syncDirection === "PULL"}
            shouldStopPropagation={true}
            size="medium"
            dsVersion="2.0"
          >
            {"PULL"}
          </RadioButton>
          <RadioButton
            key={"PUSH"}
            onChange={() => {
              setSyncDirection("PUSH");
            }}
            checked={syncDirection === "PUSH"}
            shouldStopPropagation={true}
            size="medium"
            dsVersion="2.0"
          >
            {"PUSH"}
          </RadioButton>
        </div>
      </div>

      <div className={classes.switchContainer}>
        <div>{t("integration:attendance_sync")}</div>
        <div className={classes.actionItems}>
          <ToggleSwitch
            checked={orgLevelToggleMap[ATTENDANCE_SYNC_SUB_TYPES.BASE_SYNC]}
            onChange={() =>
              handleOrgLevelToggle({
                addOnSubType: ATTENDANCE_SYNC_SUB_TYPES.BASE_SYNC,
                syncDirection: syncDirection,
              })
            }
            disabled={_.isEmpty(syncDirection)}
          />
        </div>
      </div>

      <div className={classes.switchContainer}>
        <div>{t("integration:lr_architecture_enable")}</div>
        <div className={classes.actionItems}>
          <ToggleSwitch
            checked={
              orgLevelToggleMap[ATTENDANCE_SYNC_SUB_TYPES.LR_ARCH_ENABLE]
            }
            onChange={() =>
              handleOrgLevelToggle({
                addOnSubType: ATTENDANCE_SYNC_SUB_TYPES.LR_ARCH_ENABLE,
              })
            }
          />
        </div>
      </div>

      {fullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const internalToolGenericDataVariables = getInternalToolGenericDataVariables({
    orgId,
    addOnType: ATTENDANCE_SYNC,
  });

  return {
    isData: true,
    isLoading: false,
    internalToolGenericDataVariables,
    orgId,
  };
};

const mapActionCreators = { updateAddOnStatus };

const AttendanceSyncWithLoader = compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    skip: ({ orgId }) => _.isEmpty(orgId),
    options: ({ internalToolGenericDataVariables }) => ({
      variables: internalToolGenericDataVariables,
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const data = _.get(getInternalToolGenericData, "platform");

      const queryData = _.get(data, "internalToolsGenericQuery.data", []);
      const attSyncDirection = _.get(queryData, "syncDirection", "");

      return {
        configValues: queryData,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getInternalToolGenericData.networkStatus
          ) || isLoading,
        attSyncDirection,
      };
    },
  }),
  withLoader
)(AttendanceSyncWrapper);

const AttendanceSync = ({
  orgId,
  closeModal,
  onSuccessAddOnConfigChange = null,
}) => {
  const { t } = useI18n(["integration", "common"]);

  // why fixed height and width?
  return (
    <Modal width="580px" height="550px" isOpen onClose={closeModal}>
      <Modal.Header title={t("integration:attendance_sync")} isClosable />
      <Modal.Body isScrollable>
        <AttendanceSyncWithLoader
          closeModal={closeModal}
          orgId={orgId}
          onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.rightFooterContainer}>
            <Button
              _legacy_variant="outlined-subtle"
              variant="neutral"
              type="outlined"
              size="large"
              onClick={closeModal}
            >
              {t("common:close")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AttendanceSync;
